/**
 * forecastSectionExtraction.js
 *
 * MINSKY-16818 (prompt dedup): extracts the plain text of an existing "FORECAST" section from
 * a report's plain ProseMirror JSON document (as returned by `editor.getJSON()`), so a
 * newly-started forecast job can be told what was already added to the document and avoid
 * repeating it.
 *
 * Mirrors `Forecast.jsx`'s `findForecastInsertPoint` boundary logic (top-level heading match,
 * bounded by the next top-level heading at the same-or-shallower level) but walks a plain JSON
 * tree instead of a live ProseMirror `Node`, since `getDocumentContent()` returns JSON
 * (`editor.getJSON()`/`doc.toJSON()`), not a `Node` with `.descendants(...)`.
 */

const FORECAST_HEADING_TEXT = 'FORECAST';

/** Concatenate every `text` leaf under a plain PM JSON node, depth-first. */
function flattenNodeText(node) {
    if (!node) return '';
    if (typeof node.text === 'string') return node.text;
    if (Array.isArray(node.content)) {
        return node.content.map(flattenNodeText).join('');
    }
    return '';
}

/**
 * Extract the plain text of the existing "FORECAST" section (heading text match, case-sensitive
 * like `findForecastInsertPoint`) from a plain ProseMirror JSON doc, if present.
 *
 * Each top-level node following the heading becomes one line (its own text flattened,
 * whitespace-trimmed); lines are joined with `\n`. Collection stops at the next top-level
 * heading whose level is <= the FORECAST heading's own level (or the end of the document if
 * there is none), matching `findForecastInsertPoint`'s boundary.
 *
 * @param {Object} docJson - Plain ProseMirror JSON doc (`{type: 'doc', content: [...]}`)
 * @param {string} [headingText] - Heading text to match (defaults to "FORECAST")
 * @returns {string} The section's flattened plain text, or '' if no such heading exists / the section is empty
 */
export function extractForecastSectionText(docJson, headingText = FORECAST_HEADING_TEXT) {
    if (!docJson || !Array.isArray(docJson.content)) {
        return '';
    }

    const topLevel = docJson.content;
    let forecastIndex = -1;
    let forecastLevel = null;

    for (let i = 0; i < topLevel.length; i++) {
        const node = topLevel[i];
        if (node?.type === 'heading' && flattenNodeText(node).trim() === headingText) {
            forecastIndex = i;
            forecastLevel = node.attrs?.level;
            break;
        }
    }

    if (forecastIndex === -1) {
        return '';
    }

    const lines = [];
    for (let i = forecastIndex + 1; i < topLevel.length; i++) {
        const node = topLevel[i];
        if (node?.type === 'heading' && (node.attrs?.level ?? Infinity) <= forecastLevel) {
            break;
        }
        const text = flattenNodeText(node).trim();
        if (text) {
            lines.push(text);
        }
    }

    return lines.join('\n');
}
